import type { FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { Autocomplete, TextField } from '@mui/material';
import type { FieldBaseProps } from './fields/types';

export interface EmailChipsFieldProps<T extends FieldValues> extends FieldBaseProps<T> {
  placeholder?: string;
}

/** Basic email well-formedness (mirrors the server's `LooksLikeEmail` intent). */
function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/**
 * Multi-value email entry for CC addresses (the Polaris kit `WzEmailChips`) —
 * an MUI `freeSolo` `Autocomplete` that renders each address as a chip.
 * Enter/comma/blur commits a token; only well-formed, de-duplicated addresses
 * are kept (matching the server's CC validation). RHF-bound; the field value is
 * a `string[]`.
 */
export function EmailChipsField<T extends FieldValues>({
  control,
  name,
  label,
  helperText,
  disabled,
  placeholder,
}: EmailChipsFieldProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const values: string[] = Array.isArray(field.value) ? field.value : [];
        return (
          <Autocomplete
            multiple
            freeSolo
            options={[] as string[]}
            value={values}
            disabled={disabled}
            onChange={(_e, next) => {
              const cleaned = (next as string[])
                .map((v) => v.trim().replace(/,$/, ''))
                .filter((v) => v.length > 0 && looksLikeEmail(v));
              field.onChange(Array.from(new Set(cleaned)));
            }}
            onBlur={field.onBlur}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={label}
                placeholder={values.length === 0 ? placeholder : undefined}
                error={!!fieldState.error}
                helperText={fieldState.error?.message ?? helperText}
              />
            )}
          />
        );
      }}
    />
  );
}
